(() => {
  const ROOT_SELECTOR = '[data-mr-rbs-catalogue]';
  const EN_RARITY_ORDER = ['Showcase', 'Epic', 'Rare', 'Uncommon', 'Common'];
  const ZH_RARITY_ORDER = ['史詩', '異畫', '稀有', '不凡', '普通'];
  const EN_RARITY_KEYS = {
    Showcase: 'rarityShowcase', Epic: 'rarityEpic', Rare: 'rarityRare',
    Uncommon: 'rarityUncommon', Common: 'rarityCommon'
  };
  const EN_TYPE_KEYS = {
    Unit: 'typeUnit', Spell: 'typeSpell', Rune: 'typeRune', Gear: 'typeGear',
    Legend: 'typeLegend', Battlefield: 'typeBattlefield'
  };

  const format = (template, values) => Object.entries(values).reduce(
    (output, [key, value]) => output.split(`[[${key}]]`).join(String(value)), template
  );

  const normalise = (value) => String(value || '').normalize('NFKC').trim().toLocaleLowerCase();

  const parseCopy = (root) => {
    try {
      return JSON.parse(root.querySelector('[data-mr-rbs-copy]').textContent);
    } catch (error) {
      return {};
    }
  };

  const parseSetCounts = (value) => Object.fromEntries(String(value || '').split(',').map((entry) => {
    const [setCode, count] = entry.split(':');
    return [setCode, Number.parseInt(count, 10)];
  }));

  const isTraditionalCatalogue = (mode) => mode === 'zh_hant' || mode === 'zh_hans';

  const normaliseCatalogueData = (mode) => {
    if (!isTraditionalCatalogue(mode)) return window.MoonroadRiftboundCatalogue;
    const payload = window.MoonroadRiftboundCatalogueZhHans;
    if (!payload || payload.schemaVersion !== 'rbs-zh-hant-display-v1' || !Array.isArray(payload.cards)) return null;
    return payload.cards.map((record) => ({
      nameOfficial: record[0],
      nameZhHant: record[1],
      name: record[1],
      code: record[2],
      setCode: record[3],
      setName: record[3],
      rarity: record[4],
      printing: record[5],
      cardTypes: record[6],
      language: 'zh-Hant',
      image: { src: record[7], alt: `${record[1]}（${record[2]}）卡圖暫用官方簡體中文版本作參考` }
    }));
  };

  const validCard = (card, mode, allowedSets) => {
    if (!card || typeof card !== 'object' || !card.name || !card.code || !allowedSets.includes(card.setCode)) return false;
    if (!card.rarity || !Array.isArray(card.cardTypes) || !card.cardTypes.length) return false;
    if (!card.image || !card.image.src || !card.image.alt) return false;
    if (isTraditionalCatalogue(mode) && (card.language !== 'zh-Hant' || !card.nameOfficial || !card.nameZhHant)) return false;
    if (!isTraditionalCatalogue(mode) && card.language !== 'en') return false;

    try {
      const imageUrl = new URL(card.image.src);
      const expectedHost = isTraditionalCatalogue(mode) ? 'cdn.playloltcg.com' : 'cdn.shopify.com';
      return imageUrl.protocol === 'https:' && imageUrl.hostname === expectedHost;
    } catch (error) {
      return false;
    }
  };

  const imageSrcset = (source, mode) => {
    if (isTraditionalCatalogue(mode)) return '';
    try {
      const url = new URL(source);
      return [160, 240, 320, 400].map((width) => {
        url.searchParams.set('width', String(width));
        return `${url.toString()} ${width}w`;
      }).join(', ');
    } catch (error) {
      return '';
    }
  };

  const readState = (defaultSet) => {
    const parameters = new URL(window.location.href).searchParams;
    return {
      set: (parameters.get('set') || defaultSet).toUpperCase(),
      search: (parameters.get('q') || '').slice(0, 120),
      type: (parameters.get('type') || '').slice(0, 80),
      card: (parameters.get('card') || '').slice(0, 100)
    };
  };

  const stateUrl = (state) => {
    const url = new URL(window.location.href);
    url.search = '';
    url.searchParams.set('set', state.set || 'OGN');
    if (state.card) {
      url.searchParams.set('card', state.card);
    } else {
      if (state.search) url.searchParams.set('q', state.search);
      if (state.type) url.searchParams.set('type', state.type);
    }
    return url;
  };

  const translatedType = (type, copy, mode) => isTraditionalCatalogue(mode) ? type : (copy[EN_TYPE_KEYS[type]] || type);
  const translatedRarity = (rarity, copy, mode) => isTraditionalCatalogue(mode)
    ? rarity
    : (copy[EN_RARITY_KEYS[rarity]] || copy.rarityOther || rarity);

  const printingFor = (card, copy, mode) => {
    if (isTraditionalCatalogue(mode)) return card.printing && card.printing !== '平卡' ? card.printing : '';
    if (card.printing && normalise(card.printing) !== 'base') return String(card.printing);
    return card.rarity === 'Showcase' ? (copy.printingShowcase || '特別版 Showcase') : '';
  };

  const initialiseCatalogue = (root) => {
    if (root.dataset.mrRbsReady === 'true') return;
    root.dataset.mrRbsReady = 'true';

    const mode = root.dataset.catalogueMode || 'en';
    const isZhHant = isTraditionalCatalogue(mode);
    const copy = parseCopy(root);
    const cards = normaliseCatalogueData(mode);
    const expectedCount = Number.parseInt(root.dataset.expectedCount, 10);
    const allowedSets = String(root.dataset.allowedSets || 'OGN').split(',');
    const expectedSetCounts = parseSetCounts(root.dataset.setCounts);
    const defaultSet = allowedSets[0];
    const rarityOrder = isZhHant ? ZH_RARITY_ORDER : EN_RARITY_ORDER;
    const bandsHost = root.querySelector('[data-mr-rbs-bands]');
    const chipsHost = root.querySelector('[data-mr-rbs-rarity-chips]');
    const cardTemplate = root.querySelector('[data-mr-rbs-card-template]');
    const browse = root.querySelector('[data-mr-rbs-browse]');
    const tools = root.querySelector('[data-mr-rbs-tools]');
    const setInput = root.querySelector('[data-mr-rbs-set-input]');
    const searchInput = root.querySelector('[data-mr-rbs-search]');
    const typeSelect = root.querySelector('[data-mr-rbs-type]');
    const clearButton = root.querySelector('[data-mr-rbs-clear]');
    const empty = root.querySelector('[data-mr-rbs-empty]');
    const emptyClear = root.querySelector('[data-mr-rbs-empty-clear]');
    const resultCount = root.querySelector('[data-mr-rbs-result-count]');
    const setCount = root.querySelector('[data-mr-rbs-set-count]');
    const error = root.querySelector('[data-mr-rbs-error]');
    const resetLink = root.querySelector('[data-mr-rbs-reset-link]');
    const detail = root.querySelector('[data-mr-rbs-detail]');
    const detailSurface = root.querySelector('[data-mr-rbs-detail-surface]');
    const detailMissing = root.querySelector('[data-mr-rbs-detail-missing]');
    const detailBack = root.querySelector('[data-mr-rbs-detail-back]');
    const detailTitle = root.querySelector('[data-mr-rbs-detail-title]');
    let returnFocusCode = '';
    let browseScrollPosition = 0;
    let state = readState(defaultSet);

    const showDataError = () => {
      bandsHost.replaceChildren();
      bandsHost.setAttribute('aria-busy', 'false');
      chipsHost.replaceChildren();
      resultCount.textContent = '';
      empty.hidden = true;
      error.hidden = false;
      error.querySelector('[data-mr-rbs-error-title]').textContent = copy.dataErrorTitle || '卡牌資料暫時未能顯示';
      error.querySelector('[data-mr-rbs-error-body]').textContent = copy.dataErrorBody || '';
    };

    const actualSetCounts = {};
    if (Array.isArray(cards)) cards.forEach((card) => { actualSetCounts[card.setCode] = (actualSetCounts[card.setCode] || 0) + 1; });
    const countsMatch = allowedSets.every((setCode) => actualSetCounts[setCode] === expectedSetCounts[setCode])
      && Object.keys(actualSetCounts).every((setCode) => allowedSets.includes(setCode));

    if (
      !Array.isArray(cards) || cards.length !== expectedCount || !countsMatch
      || !cards.every((card) => validCard(card, mode, allowedSets))
      || new Set(cards.map((card) => card.code)).size !== cards.length
      || !bandsHost || !chipsHost || !cardTemplate
    ) {
      showDataError();
      return;
    }

    const cardByCode = new Map(cards.map((card) => [card.code, card]));
    const cardsForSet = () => cards.filter((card) => card.setCode === state.set);

    const syncSetChrome = () => {
      setInput.value = state.set;
      setCount.textContent = expectedSetCounts[state.set];
      resetLink.href = stateUrl({ set: state.set }).toString();
      detailBack.href = stateUrl({ set: state.set }).toString();
      detailBack.textContent = `← 返回 ${state.set} 卡表`;
      root.querySelectorAll('[data-mr-rbs-set-tabs] a[data-set-code]').forEach((tab) => {
        if (tab.dataset.setCode === state.set) tab.setAttribute('aria-current', 'page');
        else tab.removeAttribute('aria-current');
      });
    };

    const populateTypes = () => {
      const types = [...new Set(cardsForSet().flatMap((card) => card.cardTypes))]
        .sort((a, b) => translatedType(a, copy, mode).localeCompare(translatedType(b, copy, mode), 'zh-Hant'));
      const fragment = document.createDocumentFragment();
      const all = document.createElement('option');
      all.value = '';
      all.textContent = copy.allTypes || '所有卡種';
      fragment.append(all);
      types.forEach((type) => {
        const option = document.createElement('option');
        option.value = type;
        option.textContent = translatedType(type, copy, mode);
        fragment.append(option);
      });
      typeSelect.replaceChildren(fragment);
      state.type = types.includes(state.type) ? state.type : '';
    };

    const createCard = (card, eager) => {
      const article = cardTemplate.content.firstElementChild.cloneNode(true);
      const link = article.querySelector('[data-mr-rbs-card-link]');
      const image = article.querySelector('[data-mr-rbs-card-image]');
      const printing = printingFor(card, copy, mode);
      const nameLabel = article.querySelector('[data-mr-rbs-card-name-label]');

      link.href = stateUrl({ set: card.setCode, card: card.code }).toString();
      link.dataset.cardCode = card.code;
      link.setAttribute('aria-label', `${copy.viewCard || '查看卡牌資料'}：${card.name}（${card.code}）`);
      article.querySelector('[data-mr-rbs-card-code]').textContent = card.code;
      const cardName = article.querySelector('[data-mr-rbs-card-name]');
      cardName.textContent = card.name;
      if (isZhHant) cardName.setAttribute('lang', 'zh-Hant');
      article.querySelector('[data-mr-rbs-card-type]').textContent = card.cardTypes
        .map((type) => translatedType(type, copy, mode)).join(' / ');
      if (isZhHant) {
        nameLabel.textContent = copy.moonroadTranslatedName || 'MOONROAD 繁中譯名';
        nameLabel.hidden = false;
      }
      if (printing) {
        const badge = article.querySelector('[data-mr-rbs-card-printing]');
        badge.textContent = printing;
        badge.hidden = false;
      }

      image.src = card.image.src;
      const srcset = imageSrcset(card.image.src, mode);
      if (srcset) image.srcset = srcset;
      image.width = card.image.width || 744;
      image.height = card.image.height || 1039;
      image.alt = card.image.alt;
      image.decoding = 'async';
      image.loading = eager ? 'eager' : 'lazy';
      image.fetchPriority = eager ? 'high' : 'low';
      return article;
    };

    const matchingCards = () => {
      const query = normalise(state.search);
      return cardsForSet().filter((card) => {
        const searchMatch = !query || normalise(card.name).includes(query)
          || normalise(card.nameOfficial).includes(query) || normalise(card.code).includes(query);
        return searchMatch && (!state.type || card.cardTypes.includes(state.type));
      });
    };

    const renderBands = () => {
      syncSetChrome();
      populateTypes();
      searchInput.value = state.search;
      typeSelect.value = state.type;
      clearButton.hidden = !state.search && !state.type;

      const matches = matchingCards();
      const grouped = new Map();
      matches.forEach((card) => {
        if (!grouped.has(card.rarity)) grouped.set(card.rarity, []);
        grouped.get(card.rarity).push(card);
      });
      const unknown = [...grouped.keys()].filter((rarity) => !rarityOrder.includes(rarity)).sort();
      const visibleRarities = [...rarityOrder, ...unknown].filter((rarity) => grouped.has(rarity));
      const bandsFragment = document.createDocumentFragment();
      const chipsFragment = document.createDocumentFragment();
      let renderedCards = 0;

      visibleRarities.forEach((rarity, index) => {
        const rarityCards = grouped.get(rarity);
        const band = document.createElement('section');
        const raritySlug = encodeURIComponent(rarity).replace(/%/g, '').toLowerCase();
        const bandId = `mr-rbs-band-${state.set.toLowerCase()}-${raritySlug}`;
        const headingId = `${bandId}-heading`;
        band.className = 'mr-rbs-band';
        band.id = bandId;
        band.setAttribute('aria-labelledby', headingId);
        band.style.setProperty('--mr-rbs-band-size', `${Math.ceil(rarityCards.length / 2) * 390 + 66}px`);

        const head = document.createElement('header');
        head.className = 'mr-rbs-band__head';
        const number = document.createElement('span');
        number.className = 'mr-rbs-band__number';
        number.textContent = String(index + 1).padStart(2, '0');
        const heading = document.createElement('h2');
        heading.id = headingId;
        heading.textContent = translatedRarity(rarity, copy, mode);
        const count = document.createElement('span');
        count.className = 'mr-rbs-band__count';
        count.textContent = `${rarityCards.length} 張`;
        head.append(number, heading, count);

        const grid = document.createElement('div');
        grid.className = 'mr-rbs-band__grid';
        rarityCards.forEach((card) => {
          grid.append(createCard(card, renderedCards === 0));
          renderedCards += 1;
        });
        band.append(head, grid);
        bandsFragment.append(band);

        const chip = document.createElement('a');
        chip.href = `#${bandId}`;
        chip.textContent = translatedRarity(rarity, copy, mode);
        const chipCount = document.createElement('span');
        chipCount.textContent = rarityCards.length;
        chip.append(chipCount);
        chipsFragment.append(chip);
      });

      bandsHost.replaceChildren(bandsFragment);
      chipsHost.replaceChildren(chipsFragment);
      bandsHost.setAttribute('aria-busy', 'false');
      error.hidden = true;
      empty.hidden = matches.length !== 0;
      bandsHost.hidden = matches.length === 0;
      chipsHost.hidden = matches.length === 0;
      resultCount.textContent = format(copy.resultCount || '顯示 [[count]] 張卡牌', { count: matches.length });
    };

    const setDetailImage = (card) => {
      const image = root.querySelector('[data-mr-rbs-detail-image]');
      image.src = card.image.src;
      const srcset = imageSrcset(card.image.src, mode);
      if (srcset) image.srcset = srcset;
      else image.removeAttribute('srcset');
      image.sizes = '(min-width: 768px) 42vw, 92vw';
      image.width = card.image.width || 744;
      image.height = card.image.height || 1039;
      image.alt = card.image.alt;
      image.decoding = 'async';
      image.loading = 'eager';
      image.fetchPriority = 'high';
    };

    const showDetail = (card, focusHeading = false) => {
      syncSetChrome();
      browse.hidden = true;
      detail.hidden = false;
      detailSurface.hidden = !card;
      detailMissing.hidden = Boolean(card);
      if (!card) {
        detailMissing.textContent = copy.detailUnavailable || '搵唔到呢張卡牌。';
        return;
      }

      const printing = printingFor(card, copy, mode);
      const printingRow = root.querySelector('[data-mr-rbs-detail-printing-row]');
      const nameLabel = root.querySelector('[data-mr-rbs-detail-name-label]');
      root.querySelector('[data-mr-rbs-detail-code]').textContent = card.code;
      detailTitle.textContent = card.name;
      if (isZhHant) detailTitle.setAttribute('lang', 'zh-Hant');
      else detailTitle.removeAttribute('lang');
      nameLabel.hidden = !isZhHant;
      nameLabel.textContent = copy.moonroadTranslatedName || 'MOONROAD 繁中譯名';
      root.querySelector('[data-mr-rbs-detail-set]').textContent = card.setCode;
      root.querySelector('[data-mr-rbs-detail-rarity]').textContent = translatedRarity(card.rarity, copy, mode);
      root.querySelector('[data-mr-rbs-detail-type]').textContent = card.cardTypes
        .map((type) => translatedType(type, copy, mode)).join(' / ');
      root.querySelector('[data-mr-rbs-detail-language]').textContent = isZhHant
        ? (copy.languageZhHant || '繁體中文') : (copy.languageEnglish || '英文');
      root.querySelector('[data-mr-rbs-detail-printing]').textContent = printing;
      printingRow.hidden = !printing;
      setDetailImage(card);

      if (focusHeading) window.requestAnimationFrame(() => {
        detail.scrollIntoView({ block: 'start', behavior: 'auto' });
        detailTitle.focus({ preventScroll: true });
      });
    };

    const showBrowse = (restoreFocus = false) => {
      detail.hidden = true;
      browse.hidden = false;
      renderBands();
      if (restoreFocus && returnFocusCode) {
        const target = [...bandsHost.querySelectorAll('[data-mr-rbs-card-link]')]
          .find((link) => link.dataset.cardCode === returnFocusCode) || searchInput;
        window.requestAnimationFrame(() => {
          window.scrollTo({ top: browseScrollPosition, behavior: 'auto' });
          target.focus({ preventScroll: true });
        });
      }
    };

    const syncFromLocation = (options = {}) => {
      state = readState(defaultSet);
      let replaceUrl = false;
      if (!allowedSets.includes(state.set)) {
        state.set = defaultSet;
        replaceUrl = true;
      }
      const selectedCard = state.card ? cardByCode.get(state.card) : null;
      if (selectedCard && selectedCard.setCode !== state.set) {
        state.set = selectedCard.setCode;
        replaceUrl = true;
      }
      if (replaceUrl) window.history.replaceState(window.history.state || {}, '', stateUrl(state));
      if (state.card) showDetail(selectedCard, options.focusHeading);
      else showBrowse(options.restoreFocus);
    };

    const applyBrowseState = () => {
      state = { set: state.set, search: searchInput.value.trim().slice(0, 120), type: typeSelect.value, card: '' };
      window.history.pushState({ mrRbsBrowse: true }, '', stateUrl(state));
      showBrowse();
    };

    tools.addEventListener('submit', (event) => { event.preventDefault(); applyBrowseState(); });
    typeSelect.addEventListener('change', applyBrowseState);
    clearButton.addEventListener('click', () => { searchInput.value = ''; typeSelect.value = ''; applyBrowseState(); });
    emptyClear.addEventListener('click', () => {
      searchInput.value = ''; typeSelect.value = ''; applyBrowseState(); searchInput.focus();
    });

    bandsHost.addEventListener('click', (event) => {
      const link = event.target.closest('[data-mr-rbs-card-link]');
      if (!link || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      const card = cardByCode.get(link.dataset.cardCode);
      if (!card) return;
      event.preventDefault();
      returnFocusCode = card.code;
      browseScrollPosition = window.scrollY;
      state = { set: card.setCode, search: '', type: '', card: card.code };
      window.history.pushState({ mrRbsCard: true }, '', link.href);
      showDetail(card, true);
    });

    detailBack.addEventListener('click', (event) => {
      if (!window.history.state || !window.history.state.mrRbsCard) return;
      event.preventDefault();
      window.history.back();
    });

    root.querySelector('[data-mr-rbs-set-tabs]').addEventListener('keydown', (event) => {
      if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
      const tabs = [...event.currentTarget.querySelectorAll('a[href]')];
      if (!tabs.length) return;
      event.preventDefault();
      const current = Math.max(0, tabs.indexOf(document.activeElement));
      let next = current;
      if (event.key === 'Home') next = 0;
      if (event.key === 'End') next = tabs.length - 1;
      if (event.key === 'ArrowLeft') next = (current - 1 + tabs.length) % tabs.length;
      if (event.key === 'ArrowRight') next = (current + 1) % tabs.length;
      tabs[next].focus();
    });

    window.addEventListener('popstate', () => syncFromLocation({ restoreFocus: true }));
    syncFromLocation();
  };

  const initialiseAll = (scope = document) => scope.querySelectorAll(ROOT_SELECTOR).forEach(initialiseCatalogue);
  document.addEventListener('DOMContentLoaded', () => initialiseAll());
  document.addEventListener('shopify:section:load', (event) => initialiseAll(event.target));
})();
